import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ChangePasswordModal = ({ isOpen, onClose, onSubmit, isSubmitting, error }) => {
  const { t } = useTranslation('settings');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setValidationError('');
    }
  }, [isOpen]);

  const handleSubmit = (event) => {
    event.preventDefault();
    setValidationError('');

    if (!currentPassword || !newPassword || !confirmPassword) {
      setValidationError(t('changePasswordModal.fillAllFields'));
      return;
    }

    if (newPassword.length < 6) {
      setValidationError(t('changePasswordModal.minLength'));
      return;
    }

    if (newPassword !== confirmPassword) {
      setValidationError(t('changePasswordModal.mismatch'));
      return;
    }

    onSubmit({
      currentPassword,
      newPassword,
    });
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
        onClick={onClose}
      >
        <motion.form
          onClick={(event) => event.stopPropagation()}
          onSubmit={handleSubmit}
          initial={{ opacity: 0, scale: 0.94, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 20 }}
          transition={{ type: 'spring', duration: 0.35 }}
          className="w-full max-w-lg bg-gradient-to-br from-white via-white to-gray-50 dark:from-dark-800 dark:via-dark-800 dark:to-dark-900 border border-gray-200/70 dark:border-white/10/60 rounded-2xl shadow-2xl overflow-hidden"
        >
          <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 dark:border-white/10/60">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-[#2563eb]/15 text-[#3d8ef0] dark:text-primary-300 flex items-center justify-center">
                <Lock className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)] dark:text-white">{t('changePasswordModal.title')}</h3>
                <p className="text-sm text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)]">
                  {t('changePasswordModal.subtitle')}
                </p>
              </div>
            </div>
            <motion.button
              type="button"
              onClick={onClose}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
              className="p-2 rounded-xl text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] dark:text-[var(--color-text-muted)] dark:hover:text-gray-200 hover:bg-white/[0.05]/80 dark:hover:bg-[#1e2d47]/70 transition-all"
              aria-label={t('changePasswordModal.close')}
            >
              <X className="w-5 h-5" />
            </motion.button>
          </div>

          <div className="px-6 py-6 space-y-5">
            <div className="space-y-1">
              <label className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">
                {t('changePasswordModal.currentPasswordLabel')}
              </label>
              <input
                type="password"
                autoComplete="current-password"
                className="w-full px-4 py-3 rounded-xl border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50/70 focus:border-primary-400 transition-colors"
                placeholder="••••••••"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-1">
                <label className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">
                  {t('changePasswordModal.newPasswordLabel')}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50/70 focus:border-primary-400 transition-colors"
                  placeholder={t('changePasswordModal.newPasswordPlaceholder')}
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-[var(--color-text-secondary)] dark:text-gray-300">
                  {t('changePasswordModal.confirmPasswordLabel')}
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  className="w-full px-4 py-3 rounded-xl border border-white/10 dark:border-white/10 bg-[var(--color-bg-base)] dark:bg-[#161f31] text-[var(--color-text-primary)] dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50/70 focus:border-primary-400 transition-colors"
                  placeholder={t('changePasswordModal.confirmPasswordPlaceholder')}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                />
              </div>
            </div>

            {(validationError || error) && (
              <p className="text-sm text-red-500 bg-red-100/10 dark:bg-red-500/10 border border-red-200/40 dark:border-red-500/40 rounded-xl px-4 py-3">
                {validationError || error}
              </p>
            )}
          </div>

          <div className="px-6 py-5 border-t border-white/10 dark:border-white/10/60 bg-gray-50/80 dark:bg-[var(--color-bg-elevated)]/60 flex items-center justify-end space-x-3">
            <motion.button
              type="button"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl font-medium text-[var(--color-text-secondary)] dark:text-gray-300 hover:text-[var(--color-text-primary)] dark:hover:text-white bg-[var(--color-bg-base)] dark:bg-transparent border border-white/10 dark:border-white/10 hover:border-white/10 dark:hover:border-dark-500 transition-all"
            >
              {t('changePasswordModal.cancel')}
            </motion.button>
            <motion.button
              type="submit"
              whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
              whileTap={{ scale: isSubmitting ? 1 : 0.98 }}
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-500 hover:to-primary-600 transition-all shadow-lg shadow-primary-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('changePasswordModal.updating') : t('changePasswordModal.updatePassword')}
            </motion.button>
          </div>
        </motion.form>
      </motion.div>
    </AnimatePresence>
  );
};

export default ChangePasswordModal;


